import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { getSelectedDialectId, fetchDialect } from '../lib/dialects';
import { getProgress, getCompletedLessons, findNextLesson } from '../lib/courseProgress';
import BottomNav from '../components/BottomNav';
import { getReviewStats } from '../lib/reviewQueue';

/**
 * CoursePage —— 场景课程主页（Midoo 式教学法的主入口）
 *
 * 与旧首页的区别：
 *   旧首页是「相机」，学习路径是 拍照 → 生成故事 → 跟读，起点是"一张照片"。
 *   这里是「课程」，学习路径是 选课 → 场景对话 → 逐字纠音 → 复习，
 *   起点是"我要学会做什么"——这正是 Midoo 的教学法核心。
 *
 * 课程内容全部来自后端静态配置（config/curriculum.js），
 * 不依赖 LLM / TTS / 视觉模型，因此密钥失效时仍然完全可用。
 */

const LEVEL_STYLE = {
  beginner: {
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
    bar: 'bg-emerald-400',
  },
  intermediate: {
    badge: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
    bar: 'bg-sky-400',
  },
  advanced: {
    badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300',
    bar: 'bg-violet-400',
  },
};

export default function CoursePage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [groups, setGroups] = useState([]);
  // 用户自己生成的课程（课程生成器产出），与内置课程分开存放、单独一组展示
  const [generatedGroups, setGeneratedGroups] = useState([]);
  const [generatedCount, setGeneratedCount] = useState(0);
  // 当前方言：决定课堂里老师用什么发音，所以要显示出来
  const [currentDialect, setCurrentDialect] = useState(null);
  const [completedIds, setCompletedIds] = useState(() => new Set(getCompletedLessons()));
  const [reviewStats, setReviewStats] = useState(() => getReviewStats());

  const load = useCallback(async (dialectForLoad) => {
    setLoading(true);
    setError(null);
    try {
      // 按当前方言拉课程：不带这个参数会把 12 个方言的课全堆在一起
      const d = dialectForLoad || getSelectedDialectId();
      const response = await api.get('/api/curriculum', { params: { dialect: d } });
      const data = response.data?.data || response.data;
      setSummary(data.summary);
      setGroups(data.groups || []);
      setGeneratedGroups(data.generatedGroups || []);
      setGeneratedCount(data.generatedCount || 0);
      setCompletedIds(new Set(getCompletedLessons()));
      setReviewStats(getReviewStats());
    } catch (e) {
      console.error('[CoursePage] 加载课程失败:', e);
      setError(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * 加载当前方言信息
   *
   * 每次页面重新可见时都刷新一次 —— 用户从「选择方言」页返回时，
   * 本页并没有卸载，只有 visibility/focus 能捕捉到这个变化。
   * 用 document.visibilitychange 而不是路由事件，是因为选完是用
   * navigate(-1) 返回的，走不到路由变化。
   */
  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      const id = getSelectedDialectId();
      if (cancelled) return;
      // 方言变了 → 课程列表也要跟着重新拉，
      // 否则切到四川话后还显示着粤语的 15 课
      load(id);
      try {
        const d = await fetchDialect(id);
        if (!cancelled && d) setCurrentDialect(d);
      } catch {
        /* 静默：方言信息拉不到不影响课程浏览 */
      }
    };
    sync();
    const onVisible = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', sync);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', sync);
    };
  }, [load]);

  const progress = getProgress(summary?.totalLessons || 0);
  const nextLesson = findNextLesson(groups);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-zinc-900 dark:to-zinc-800 pb-24">
      {/* 顶部：课程总览与进度 */}
      <header className="bg-gradient-to-br from-teal-500 to-sky-500 px-6 pt-12 pb-7 dark:from-teal-700 dark:to-sky-700">
        <h1 className="text-2xl font-bold text-white">方言通 · DialectGo</h1>

        {/*
          当前方言入口。
          方言不是装饰 —— 它决定课堂里老师用什么发音（千问靠 instruction
          驱动方言，不传方言就会被读成普通话）。所以放在最显眼的位置。
        */}
        <Link
          to="/dialects"
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1.5 text-xs font-bold text-white backdrop-blur-sm active:scale-95"
        >
          <span className="material-icons !text-sm">translate</span>
          {currentDialect?.displayName || '选择方言'}
          <span className="material-icons !text-sm opacity-70">expand_more</span>
        </Link>

        <p className="mt-1.5 text-sm text-white/85">
          按场景学，学完就能用 —— 不是背单词，是完成任务
        </p>

        {summary && (
          <div className="mt-5 flex items-center gap-4">
            <div className="flex-1">
              <div className="mb-1.5 flex items-baseline justify-between">
                <span className="text-xs text-white/85">学习进度</span>
                <span className="text-xs font-bold text-white">
                  {progress.completed}/{progress.total} 课
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-white/25">
                <div
                  className="h-full rounded-full bg-white transition-all duration-500"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {summary && (
          <div className="mt-4 flex gap-4 text-[11px] text-white/80">
            <span>{summary.totalLessons} 课</span>
            <span>{summary.totalPhrases} 句目标句型</span>
            <span>约 {summary.totalDurationMin} 分钟</span>
          </div>
        )}
      </header>

      {/* 继续学习 */}
      {nextLesson && (
        <div className="mx-6 -mt-4">
          <button
            type="button"
            onClick={() => navigate(`/session/${nextLesson.id}`)}
            className="flex w-full items-center gap-4 rounded-2xl bg-white p-4 text-left shadow-md transition-transform active:scale-[0.98] dark:bg-zinc-800"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-2xl dark:bg-teal-900/40">
              {nextLesson.icon}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] font-medium text-teal-600 dark:text-teal-400">
                继续学习
              </span>
              <span className="block truncate font-bold text-zinc-800 dark:text-zinc-100">
                {nextLesson.title}
              </span>
              <span className="block truncate text-xs text-zinc-500 dark:text-zinc-400">
                {nextLesson.goal}
              </span>
            </span>
            <span className="material-icons text-teal-500">play_circle</span>
          </button>
        </div>
      )}

      {/* 错音复习入口（Midoo 教学法第四步：高频错误回流） */}
      {reviewStats.total > 0 && (
        <div className="mx-6 mt-3">
          <Link
            to="/review"
            className="flex w-full items-center gap-4 rounded-2xl bg-rose-50 p-4 text-left transition-transform active:scale-[0.98] dark:bg-rose-900/25"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/70 dark:bg-rose-900/40">
              <span className="material-icons text-rose-500 dark:text-rose-300">replay</span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] font-medium text-rose-600 dark:text-rose-300">
                错音复习
              </span>
              <span className="block font-bold text-zinc-800 dark:text-zinc-100">
                {reviewStats.characters} 个字需要重练
              </span>
            </span>
            <span className="material-icons text-rose-400">chevron_right</span>
          </Link>
        </div>
      )}

      {/* 课程列表 */}
      <main className="px-6 py-6">
        {loading && (
          <div className="py-16 text-center text-zinc-500 dark:text-zinc-400">
            <span className="material-icons mb-2 !text-4xl animate-spin">refresh</span>
            <p className="text-sm">正在加载课程…</p>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl bg-amber-50 p-5 dark:bg-amber-900/30">
            <p className="text-sm font-bold text-amber-700 dark:text-amber-300">课程加载失败</p>
            <p className="mt-1 text-xs leading-relaxed text-amber-700/80 dark:text-amber-300/80">
              课程内容需要联网获取。请检查网络后重试；如果一直失败，可能是服务暂时不可用。
            </p>
            <p className="mt-1 break-all font-mono text-[10px] text-amber-600/70 dark:text-amber-400/70">
              {error}
            </p>
            <button
              type="button"
              onClick={load}
              className="mt-3 rounded-xl bg-amber-500 px-4 py-2 text-xs font-bold text-white active:opacity-80"
            >
              重试
            </button>
          </div>
        )}

        {!loading && !error && groups.map(({ level, lessons }) => {
          const style = LEVEL_STYLE[level.id] || LEVEL_STYLE.beginner;
          return (
            <section key={level.id} className="mb-7">
              <div className="mb-3 flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${style.badge}`}>
                  {level.name}
                </span>
                <span className="text-xs text-zinc-400 dark:text-zinc-500">
                  {level.description}
                </span>
              </div>

              <div className="space-y-3">
                {lessons.map((lesson) => {
                  const done = completedIds.has(lesson.id);
                  return (
                    <button
                      key={lesson.id}
                      type="button"
                      onClick={() => navigate(`/session/${lesson.id}`)}
                      className="flex w-full items-start gap-3 rounded-2xl border border-zinc-100 bg-white p-4 text-left shadow-sm transition-transform active:scale-[0.98] dark:border-zinc-700/60 dark:bg-zinc-800"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-zinc-50 text-xl dark:bg-zinc-700/60">
                        {lesson.icon}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate font-bold text-zinc-800 dark:text-zinc-100">
                            {lesson.title}
                          </span>
                          {done && (
                            <span className="material-icons !text-base text-emerald-500">check_circle</span>
                          )}
                        </span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                          {lesson.goal}
                        </span>
                        <span className="mt-1.5 flex items-center gap-3 text-[11px] text-zinc-400 dark:text-zinc-500">
                          <span className="flex items-center gap-0.5">
                            <span className="material-icons !text-sm">schedule</span>
                            {lesson.durationMin} 分钟
                          </span>
                          <span className="flex items-center gap-0.5">
                            <span className="material-icons !text-sm">format_quote</span>
                            {lesson.phraseCount} 个句型
                          </span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </main>

      {/*
        生成课程区：内置 15 课学完之后，用户可以用课程生成器按自己的需求扩展。
        放在内置课程之后、拍照入口之前，位置符合"学完基础再扩展"的顺序。
      */}
      <section className="px-6 pb-4">
        {generatedCount > 0 && (
          <>
            <h2 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-zinc-700 dark:text-zinc-200">
              <span className="material-icons !text-lg text-primary">auto_awesome</span>
              我生成的课程
              <span className="ml-1 text-[11px] font-normal text-zinc-400">{generatedCount} 课</span>
            </h2>
            <div className="mb-4 space-y-2.5">
              {generatedGroups.flatMap(({ lessons }) =>
                lessons.map((lesson) => (
                  <button
                    key={lesson.id}
                    type="button"
                    onClick={() => navigate(`/session/${lesson.id}`)}
                    className="flex w-full items-start gap-3 rounded-2xl border border-primary/20 bg-white p-4 text-left shadow-sm transition-transform active:scale-[0.98] dark:border-primary/30 dark:bg-zinc-800"
                  >
                    <span className="text-xl">{lesson.icon}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold text-zinc-800 dark:text-zinc-100">
                        {lesson.title}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-zinc-500 dark:text-zinc-400">
                        {lesson.goal}
                      </span>
                    </span>
                    <span className="material-icons shrink-0 text-zinc-300">chevron_right</span>
                  </button>
                ))
              )}
            </div>
          </>
        )}

        <Link
          to="/generate"
          className="flex w-full items-center gap-3 rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-4 text-left transition-transform active:scale-[0.98]"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <span className="material-icons text-primary">add</span>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-primary">生成新课程</span>
            <span className="mt-0.5 block text-xs text-primary/70">
              15 课学完了？说出你想练的场景，我来生成一整节课
            </span>
          </span>
          <span className="material-icons shrink-0 text-primary/40">chevron_right</span>
        </Link>
      </section>

      {/* 底部导航抽成共用组件，避免每个页面各写一套、高亮还不一致 */}
      <BottomNav />
    </div>
  );
}
